import { useState } from "react";
import { Send, Check, MapPin, Mail } from "lucide-react";
import { submitContact } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";

const TOPICS = [
  "General enquiry",
  "Partnerships",
  "SeedKeepers",
  "Restoration Readiness",
  "Knowledge & Training",
  "Media",
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "", email: "", organisation: "", role: "", topic: "General enquiry", message: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", error: "" });
    try {
      await submitContact(form);
      setStatus({ state: "success", error: "" });
      setForm({ name: "", email: "", organisation: "", role: "", topic: "General enquiry", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Please try again.";
      setStatus({ state: "error", error: typeof msg === "string" ? msg : "Could not send." });
    }
  };

  return (
    <div data-testid="page-contact" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <SectionHeader
        overline="Contact"
        title={<>Start a <span className="italic text-ra-ochre">conversation</span>.</>}
        lede="If you're a Traditional Owner group, landholder, practitioner, researcher, agency or investor — we'd like to hear from you."
        testId="contact-header"
      />

      <div className="mt-16 grid grid-cols-12 gap-12">
        <div className="col-span-12 lg:col-span-5">
          <div className="space-y-10">
            <div>
              <div className="ra-overline">Office</div>
              <div className="flex gap-4 mt-3">
                <MapPin size={22} className="text-ra-ochre flex-shrink-0 mt-1" strokeWidth={1.4} />
                <p className="text-ra-eucalypt leading-relaxed">
                  Australia-wide — operating on Country across all states and territories.
                </p>
              </div>
            </div>

            <div>
              <div className="ra-overline">Direct</div>
              <div className="flex gap-4 mt-3">
                <Mail size={22} className="text-ra-ochre flex-shrink-0 mt-1" strokeWidth={1.4} />
                <a href="mailto:hello@restorationaustralia.com" data-testid="contact-email-link" className="ra-link">
                  hello@restorationaustralia.com
                </a>
              </div>
            </div>

            <div className="bg-ra-paper border border-ra-line p-7">
              <div className="ra-overline">Response time</div>
              <p className="ra-serif text-2xl mt-2 leading-snug">We respond to enquiries within 5 business days.</p>
              <p className="text-sm text-ra-soft mt-3">
                Urgent matters or media enquiries may be flagged via your message.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} data-testid="contact-form" className="col-span-12 lg:col-span-7 bg-ra-bone border border-ra-line p-8 lg:p-12">
          {status.state === "success" ? (
            <div data-testid="contact-success" className="text-center py-12">
              <Check size={48} className="text-ra-ochre mx-auto" strokeWidth={1.3} />
              <h3 className="ra-serif text-4xl mt-6">Thank you.</h3>
              <p className="text-ra-soft mt-4 max-w-md mx-auto">
                Your message has reached us. A member of our team will be in touch shortly.
              </p>
              <button
                type="button"
                data-testid="contact-send-another"
                onClick={() => setStatus({ state: "idle", error: "" })}
                className="ra-btn-ghost mt-8"
              >
                Send another
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Full name" required>
                  <input data-testid="contact-input-name" required value={form.name} onChange={update("name")} className="ra-input" />
                </Field>
                <Field label="Email" required>
                  <input data-testid="contact-input-email" type="email" required value={form.email} onChange={update("email")} className="ra-input" />
                </Field>
                <Field label="Organisation">
                  <input data-testid="contact-input-organisation" value={form.organisation} onChange={update("organisation")} className="ra-input" />
                </Field>
                <Field label="Role">
                  <input data-testid="contact-input-role" value={form.role} onChange={update("role")} className="ra-input" />
                </Field>
              </div>

              <Field label="Topic" className="mt-6">
                <select data-testid="contact-input-topic" value={form.topic} onChange={update("topic")} className="ra-input cursor-pointer">
                  {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>

              <Field label="Message" required className="mt-6">
                <textarea
                  data-testid="contact-input-message"
                  required rows={6}
                  value={form.message}
                  onChange={update("message")}
                  className="ra-input resize-none"
                  placeholder="Tell us about your project, organisation, or how we can help."
                />
              </Field>

              {status.state === "error" && (
                <p data-testid="contact-error" className="mt-5 text-sm text-[#a3492c]">{status.error}</p>
              )}

              <div className="mt-8 flex items-center justify-between">
                <p className="text-xs text-ra-light max-w-xs">We treat your information confidentially and never share it without consent.</p>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={status.state === "loading"}
                  className="ra-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status.state === "loading" ? "Sending…" : "Send message"} <Send size={14} />
                </button>
              </div>
            </>
          )}
        </form>
      </div>

      <style>{`
        .ra-input {
          width: 100%;
          background: rgb(var(--ra-paper));
          border: 1px solid rgb(var(--ra-line));
          padding: 0.85rem 1rem;
          color: rgb(var(--ra-eucalypt));
          font-family: "Outfit", sans-serif;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 200ms ease;
        }
        .ra-input:focus {
          border-color: rgb(var(--ra-ochre));
        }
      `}</style>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="ra-overline mb-2 text-ra-soft">
        {label} {required && <span className="text-ra-ochre">*</span>}
      </div>
      {children}
    </label>
  );
}
